import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Progress } from '../ui/progress';
import { Target, Flame, Check, Zap, Clock, ArrowRight, Circle } from 'lucide-react';

// Calcule le n° de défi du jour (1-366) — change automatiquement chaque jour
const dayOfYear = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  return Math.floor((now - start) / 86400000);
};

// Temps restant jusqu'à minuit
const timeUntilEndOfDay = () => {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const diff = end - now;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return { h, m, expired: diff < 0 };
};

const DailyChallengeCard = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(timeUntilEndOfDay());

  useEffect(() => {
    fetchChallenge();
    const dataInterval = setInterval(fetchChallenge, 30000);
    const tickInterval = setInterval(() => setCountdown(timeUntilEndOfDay()), 60000);
    return () => { clearInterval(dataInterval); clearInterval(tickInterval); };
  }, []);

  const fetchChallenge = async () => {
    try {
      const res = await api.get('/daily-challenge');
      setChallenge(res.data);
    } catch (e) {
      setChallenge(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !challenge) return null;

  const isCompleted = challenge.is_completed;
  const challengeNum = String(dayOfYear()).padStart(3, '0');
  const current = challenge.current_count ?? 0;
  const target = challenge.target_count ?? 1;

  return (
    <div
      className={`daily-challenge-fiche my-8 ${isCompleted ? 'is-completed' : 'is-active'}`}
      data-testid="daily-challenge-card"
    >
      {/* Top stripe — filet or premium ou teal completed */}
      <span className="daily-challenge-stripe" aria-hidden="true" />

      {/* Sticker tampon en haut */}
      <div className="daily-challenge-sticker">
        <span className="dot" />
        <span className="text">
          Défi du jour <span className="num">·  N°{challengeNum}</span>
        </span>
      </div>

      <div className="daily-challenge-body">
        {/* Colonne gauche : titre + description + countdown */}
        <div className="daily-challenge-left">
          <div className="flex items-center gap-3">
            <div className={`icon-circle ${isCompleted ? 'is-teal' : 'is-gold'}`}>
              {isCompleted ? <Check className="w-6 h-6" /> : <Target className="w-6 h-6" />}
            </div>
            <div className="min-w-0">
              <h3 className="daily-challenge-title">
                {isCompleted ? 'Défi complété' : challenge.title}
              </h3>
              {!isCompleted && challenge.description && (
                <p className="daily-challenge-desc">{challenge.description}</p>
              )}
            </div>
          </div>

          {/* Ligne info : countdown + streak + XP */}
          <div className="daily-challenge-meta">
            {!isCompleted && !countdown.expired && (
              <span className="daily-challenge-countdown">
                <Clock className="w-3.5 h-3.5" />
                Expire dans <strong>{countdown.h}h {String(countdown.m).padStart(2, '0')}min</strong>
              </span>
            )}
            {challenge.streak > 0 && (
              <span className="dossier-pill is-gold">
                <Flame className="w-3 h-3" />{challenge.streak} jour{challenge.streak > 1 ? 's' : ''}
              </span>
            )}
            <span className="dossier-pill is-ink">
              <Zap className="w-3 h-3" />+{challenge.xp_reward} XP
            </span>
          </div>

          {/* Progress bar */}
          {!isCompleted && (
            <div className="daily-challenge-progress-wrap">
              <Progress value={challenge.progress_percent} className="h-1.5 daily-challenge-progress" />
            </div>
          )}

          {/* Étapes (défis en deux temps : inscription puis présence) */}
          {!isCompleted && challenge.steps && (
            <div className="flex flex-wrap items-center gap-3 mt-2" data-testid="daily-challenge-steps">
              {challenge.steps.map((step, i) => (
                <span
                  key={step.label}
                  className={`inline-flex items-center gap-1.5 text-xs ${step.done ? 'text-emerald-700 font-semibold' : 'text-muted-foreground'}`}
                  data-testid={`daily-challenge-step-${i}`}
                >
                  {step.done ? <Check className="w-3.5 h-3.5" /> : <Circle className="w-3 h-3" />}
                  {step.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Colonne droite : métrique géante + CTA */}
        <div className="daily-challenge-right">
          <div className="daily-challenge-metric">
            <span className="current">{current}</span>
            <span className="separator">/</span>
            <span className="target">{target}</span>
          </div>
          <div className="daily-challenge-metric-label">
            {isCompleted ? 'Objectif atteint' : (target > 1 ? 'Avancement' : 'Reste à faire')}
          </div>

          {!isCompleted && (
            <button
              type="button"
              onClick={() => navigate('/challenges')}
              className="daily-challenge-cta"
              data-testid="daily-challenge-cta"
            >
              Voir mes défis <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyChallengeCard;
